import { calcPresenterArea, getPresenterLayout, NEW_PARTICIPANT_SIZES } from './get-presenter-layout';
import type { Area, Size } from './types';

interface PresenterLayout {
    participantsListLayout: Area[];
    presenterArea: Area;
}

type GetPresenterDrawLayoutParams = {
    size: Size;
    count: number;
    maxCount: number;
    isPresenterOnly: boolean;
    isScreenSharingActive: boolean;
    margin?: number;
    isNewGrid: boolean;
};
const horizontalPadding = 40;

function oldGetPresenterDrawLayout(size: Size, count: number, maxCount: number, isPresenterOnly: boolean) {
    // Если только один участник - верхнюю полосу не показываем
    if (isPresenterOnly) {
        return {
            participantsListLayout: [],
            presenterArea: { x: 0, y: 0, width: size.width, height: size.height },
        };
    }

    const { itemWidth, itemHeight } = getPresenterItemSize({ size, maxCount });

    const sumWidth = itemWidth * count;
    const startPoint = (size.width - sumWidth) / 2;
    const participantsListLayout: Area[] = [];
    for (let i = 0; i < count; i++) {
        participantsListLayout.push({
            x: startPoint + i * itemWidth,
            y: 0,
            width: itemWidth,
            height: itemHeight,
        });
    }

    return {
        participantsListLayout,
        presenterArea: {
            x: 0,
            y: itemHeight,
            width: size.width,
            height: size.height - itemHeight,
        },
    };
}

function newGetPresenterDrawLayout({
    size,
    count,
    maxCount,
    isPresenterOnly,
    isScreenSharingActive,
    margin = 0,
}: Omit<GetPresenterDrawLayoutParams, 'isNewGrid'>) {
    if (isPresenterOnly) {
        return {
            participantsListLayout: [],
            presenterArea: calcPresenterArea(
                {
                    width: size.width - margin * 2,
                    height: size.height - margin * 2,
                },
                isScreenSharingActive,
                {}
            ),
        };
    }

    const [presenterArea, ...participantsListLayout] = getPresenterLayout({
        size,
        count: Math.min(count, maxCount),
        shouldSwapSelfView: false,
        isNewGrid: true,
        isScreenSharingActive,
        margin,
    });

    return {
        participantsListLayout,
        presenterArea,
    };
}
/**
 * @param {{width: number, height: number}} size
 * @param {number} count
 * @param {number} maxCount
 * @returns {Object}
 */

export function getPresenterDrawLayout({
    size,
    count,
    maxCount,
    isNewGrid,
    isPresenterOnly,
    isScreenSharingActive,
    margin,
}: GetPresenterDrawLayoutParams): PresenterLayout {
    return isNewGrid
        ? newGetPresenterDrawLayout({
              size,
              count: count + 1,
              maxCount: maxCount + 1,
              isPresenterOnly,
              isScreenSharingActive,
              margin,
          }) // добавляем +1 т.к. нам нужно передать фактическое количество слотов, а не только количество слотов которые сверху
        : oldGetPresenterDrawLayout(size, count, maxCount, isPresenterOnly);
}
function oldGetPresenterItemSize(size: Size, maxCount: number) {
    const itemWidth = (size.width - horizontalPadding * 2) / maxCount;
    const itemHeight = Math.floor((itemWidth / 16) * 9);
    return { itemWidth, itemHeight };
}
function newGetPresenterItemSize(isMobile: boolean) {
    const itemWidth = isMobile ? NEW_PARTICIPANT_SIZES.XS_WIDTH : NEW_PARTICIPANT_SIZES.S_WIDTH;
    const itemHeight = isMobile ? NEW_PARTICIPANT_SIZES.XS_HEIGHT : NEW_PARTICIPANT_SIZES.S_HEIGHT;
    return { itemWidth, itemHeight };
}

interface GetOldPresenterItemSizeParams {
    size: Size;
    maxCount: number;
    isNewGrid?: false;
}
interface GetNewPresenterItemSizeParams {
    size: Size;
    maxCount: number;
    isNewGrid: true;
    isMobile: boolean;
}
type GetPresenterItemSizeParams = GetOldPresenterItemSizeParams | GetNewPresenterItemSizeParams;
export function getPresenterItemSize(params: GetPresenterItemSizeParams) {
    const { size, maxCount, isNewGrid } = params;
    return isNewGrid ? newGetPresenterItemSize(params.isMobile) : oldGetPresenterItemSize(size, maxCount);
}
